import { useState } from "react";
import { motion } from "framer-motion";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Shield,
  Smartphone,
  Monitor,
  Tablet,
  MapPin,
  Clock,
  Trash2,
  Lock,
  Eye,
  EyeOff,
  Check,
  Loader2,
  AlertTriangle,
  KeyRound
} from "lucide-react";
import { cn } from "@/lib/utils";

interface Session {
  id: string;
  device: "desktop" | "mobile" | "tablet";
  browser: string;
  location: string;
  lastActive: string;
  isCurrent: boolean;
}

const mockSessions: Session[] = [
  {
    id: "1",
    device: "desktop",
    browser: "Chrome on macOS",
    location: "New York, US",
    lastActive: "Active now",
    isCurrent: true,
  },
  {
    id: "2",
    device: "mobile",
    browser: "Safari on iOS",
    location: "San Francisco, US",
    lastActive: "2 hours ago",
    isCurrent: false,
  },
  {
    id: "3",
    device: "tablet",
    browser: "Firefox on iPad",
    location: "Los Angeles, US",
    lastActive: "3 days ago",
    isCurrent: false,
  },
];

const deviceIcons = {
  desktop: Monitor,
  mobile: Smartphone,
  tablet: Tablet,
};

function SessionCard({
  session,
  onRevoke,
  isRevoking,
}: {
  session: Session;
  onRevoke: (id: string) => void;
  isRevoking: boolean;
}) {
  const DeviceIcon = deviceIcons[session.device];

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className={cn(
        "flex items-center justify-between p-4 rounded-xl border border-border",
        session.isCurrent && "bg-primary/5 border-primary/20"
      )}
    >
      <div className="flex items-center gap-4">
        <div
          className={cn(
            "p-2.5 rounded-lg",
            session.isCurrent ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"
          )}
        >
          <DeviceIcon className="w-5 h-5" />
        </div>
        <div>
          <div className="flex items-center gap-2">
            <p className="font-medium text-sm">{session.browser}</p>
            {session.isCurrent && (
              <Badge className="text-xs bg-primary/10 text-primary border-primary/20">
                Current session
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
            <span className="flex items-center gap-1">
              <MapPin className="w-3 h-3" />
              {session.location}
            </span>
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {session.lastActive}
            </span>
          </div>
        </div>
      </div>

      {!session.isCurrent && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onRevoke(session.id)}
          disabled={isRevoking}
          className="text-red-500 hover:text-red-600 hover:bg-red-500/10"
        >
          {isRevoking ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <>
              <Trash2 className="w-4 h-4 mr-1" />
              Revoke
            </>
          )}
        </Button>
      )}
    </motion.div>
  );
}

export function SecuritySettings() {
  const [sessions, setSessions] = useState(mockSessions);
  const [revokingId, setRevokingId] = useState<string | null>(null);
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [isEnabling2FA, setIsEnabling2FA] = useState(false);

  // Password form
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [passwordChanged, setPasswordChanged] = useState(false);

  const handleRevokeSession = async (id: string) => {
    setRevokingId(id);
    await new Promise((resolve) => setTimeout(resolve, 1000));
    setSessions((prev) => prev.filter((s) => s.id !== id));
    setRevokingId(null);
  };

  const handleRevokeAll = async () => {
    setRevokingId("all");
    await new Promise((resolve) => setTimeout(resolve, 1500));
    setSessions((prev) => prev.filter((s) => s.isCurrent));
    setRevokingId(null);
  };

  const handleToggle2FA = async () => {
    setIsEnabling2FA(true);
    await new Promise((resolve) => setTimeout(resolve, 1500));
    setTwoFactorEnabled(!twoFactorEnabled);
    setIsEnabling2FA(false);
  };

  const handleChangePassword = async () => {
    if (newPassword !== confirmPassword) {
      return;
    }

    setIsChangingPassword(true);
    await new Promise((resolve) => setTimeout(resolve, 2000));
    setIsChangingPassword(false);
    setPasswordChanged(true);
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setTimeout(() => setPasswordChanged(false), 3000);
  };

  const isPasswordValid =
    currentPassword.length >= 1 &&
    newPassword.length >= 8 &&
    newPassword === confirmPassword;

  return (
    <div className="space-y-6">
      {/* Active Sessions */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
      >
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Shield className="w-5 h-5" />
                  Active Sessions
                </CardTitle>
                <CardDescription>
                  Manage devices that are logged into your account
                </CardDescription>
              </div>
              {sessions.length > 1 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleRevokeAll}
                  disabled={revokingId === "all"}
                  className="text-red-500 hover:text-red-600 hover:bg-red-500/10 border-red-200 dark:border-red-800"
                >
                  {revokingId === "all" ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Revoking...
                    </>
                  ) : (
                    "Revoke all other sessions"
                  )}
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {sessions.map((session) => (
              <SessionCard
                key={session.id}
                session={session}
                onRevoke={handleRevokeSession}
                isRevoking={revokingId === session.id}
              />
            ))}

            {sessions.length === 1 && (
              <p className="text-sm text-muted-foreground text-center py-4">
                No other active sessions
              </p>
            )}
          </CardContent>
        </Card>
      </motion.div>

      {/* Change Password */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Lock className="w-5 h-5" />
              Change Password
            </CardTitle>
            <CardDescription>
              Update your password to keep your account secure
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Current Password */}
            <div className="space-y-2">
              <Label htmlFor="currentPassword">Current Password</Label>
              <div className="relative max-w-md">
                <Input
                  id="currentPassword"
                  type={showCurrentPassword ? "text" : "password"}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="Enter your current password"
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                >
                  {showCurrentPassword ? (
                    <EyeOff className="w-4 h-4" />
                  ) : (
                    <Eye className="w-4 h-4" />
                  )}
                </button>
              </div>
            </div>

            {/* New Password */}
            <div className="space-y-2">
              <Label htmlFor="newPassword">New Password</Label>
              <div className="relative max-w-md">
                <Input
                  id="newPassword"
                  type={showNewPassword ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter your new password"
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                >
                  {showNewPassword ? (
                    <EyeOff className="w-4 h-4" />
                  ) : (
                    <Eye className="w-4 h-4" />
                  )}
                </button>
              </div>
              <p className="text-xs text-muted-foreground">
                Must be at least 8 characters long
              </p>
            </div>

            {/* Confirm Password */}
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm New Password</Label>
              <div className="relative max-w-md">
                <Input
                  id="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm your new password"
                  className={cn(
                    "pr-10",
                    confirmPassword &&
                      newPassword !== confirmPassword &&
                      "border-red-500 focus-visible:ring-red-500"
                  )}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                >
                  {showConfirmPassword ? (
                    <EyeOff className="w-4 h-4" />
                  ) : (
                    <Eye className="w-4 h-4" />
                  )}
                </button>
              </div>
              {confirmPassword && newPassword !== confirmPassword && (
                <p className="text-xs text-red-500 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" />
                  Passwords do not match
                </p>
              )}
            </div>

            <div className="pt-2">
              <Button
                onClick={handleChangePassword}
                disabled={!isPasswordValid || isChangingPassword}
                className="min-w-32"
              >
                {isChangingPassword ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Changing...
                  </>
                ) : passwordChanged ? (
                  <>
                    <Check className="w-4 h-4 mr-2" />
                    Changed!
                  </>
                ) : (
                  "Change Password"
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Two-Factor Authentication */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
      >
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <KeyRound className="w-5 h-5" />
              Two-Factor Authentication
            </CardTitle>
            <CardDescription>
              Add an extra layer of security to your account
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between p-4 rounded-xl bg-muted/50">
              <div className="flex items-center gap-4">
                <div
                  className={cn(
                    "p-3 rounded-xl",
                    twoFactorEnabled
                      ? "bg-emerald-500/10 text-emerald-500"
                      : "bg-muted text-muted-foreground"
                  )}
                >
                  <Shield className="w-6 h-6" />
                </div>
                <div>
                  <p className="font-medium">
                    {twoFactorEnabled ? "2FA is enabled" : "Enable 2FA"}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {twoFactorEnabled
                      ? "Your account is protected with two-factor authentication"
                      : "Use an authenticator app for added security"}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                {isEnabling2FA && (
                  <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                )}
                <Switch
                  checked={twoFactorEnabled}
                  onCheckedChange={handleToggle2FA}
                  disabled={isEnabling2FA}
                />
              </div>
            </div>

            {twoFactorEnabled && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                className="mt-4 p-4 rounded-xl bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
              >
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4" />
                  <span className="text-sm font-medium">
                    Two-factor authentication is active. Your account is more secure.
                  </span>
                </div>
              </motion.div>
            )}
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}
